import { useState, useEffect, useRef } from "react";
import FormErrors from "@sharpana/ui/FormErrors";
import { signUp } from "../api_client/allauth";
import { Link } from "react-router-dom";
import { useConfig } from "../authHooks";
import ProviderList from "../social_account_providers/ProviderList";
import Button from "@sharpana/ui/Button";
import {
  authContainer,
  authViewportMobileSignup,
  authCard,
  authCardMobile,
  formGroup,
  compactFormGroup,
  linkText,
  linkTextCompact,
} from "./styles";
import { isMobileApp } from "@sharpana/shared/utils/web_environment";

export default function Signup() {
  const [email, setEmail] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [password2Errors, setPassword2Errors] = useState<any>([]);
  const [response, setResponse] = useState({ fetching: false, content: null });
  const config = useConfig();
  const hasProviders = config.data.socialaccount?.providers?.length > 0;

  const isOnMobileApp = isMobileApp();

  // Ref and effects to reset scroll after keyboard hides in WebView
  const cardRef = useRef<HTMLDivElement>(null);
  const lastVVH = useRef<number | null>(null);

  useEffect(() => {
    if (!isOnMobileApp) return;
    const onFocusOut = () => {
      const active = document.activeElement as HTMLElement | null;
      const isInput = !!active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);
      if (isInput) return;
      setTimeout(() => {
        cardRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    };
    document.addEventListener("focusout", onFocusOut, true);
    return () => document.removeEventListener("focusout", onFocusOut, true);
  }, [isOnMobileApp]);

  useEffect(() => {
    if (!isOnMobileApp || !window.visualViewport) return;
    const vv = window.visualViewport;
    const handler = () => {
      const h = vv.height;
      if (lastVVH.current !== null && h > lastVVH.current + 40) {
        cardRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }
      lastVVH.current = h;
    };
    vv.addEventListener("resize", handler);
    lastVVH.current = vv.height;
    return () => vv.removeEventListener("resize", handler);
  }, [isOnMobileApp]);

  function submit() {
    if (password2 !== password1) {
      setPassword2Errors([
        { param: "password2", message: "Password does not match." },
      ]);
      return;
    }
    setPassword2Errors([]);
    setResponse({ ...response, fetching: true });
    signUp({ email, password: password1 })
      .then((content) => {
        setResponse((r) => {
          return { ...r, content };
        });
      })
      .catch((e) => {
        console.error(e);
        window.alert(e);
      })
      .then(() => {
        setResponse((r) => {
          return { ...r, fetching: false };
        });
      });
  }

  return (
    <div className={isOnMobileApp ? authViewportMobileSignup : authContainer}>
      <div ref={isOnMobileApp ? cardRef : undefined} className={isOnMobileApp ? authCardMobile : authCard}>
        {isOnMobileApp ? (
          <></>
        ) : (
          <>
            <h2>Sign Up</h2>
            {hasProviders && (
              <>
                <ProviderList
                  callbackURL="/account/provider/callback"
                  flowDescription="Sign up"
                />
              </>
            )}
          </>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            margin: isOnMobileApp ? "0.5rem 0" : "1rem 0",
          }}
        >
          <hr
            style={{ flex: 1, border: "none", borderTop: "2px solid #444" }}
          />
          <span
            style={{ padding: "0 10px", color: "#b0b0b0", fontSize: "0.9rem" }}
          >
            Or use email
          </span>
          <hr
            style={{ flex: 1, border: "none", borderTop: "2px solid #444" }}
          />
        </div>

        <FormErrors errors={(response as any).content?.errors} />

        <div className={isOnMobileApp ? compactFormGroup : formGroup}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
          <FormErrors
            param="email"
            errors={(response as any).content?.errors}
          />
        </div>

        <div className={isOnMobileApp ? compactFormGroup : formGroup}>
          <label htmlFor="password1">Password</label>
          <input
            id="password1"
            autoComplete="new-password"
            value={password1}
            onChange={(e) => setPassword1(e.target.value)}
            type="password"
            required
          />
          <FormErrors
            param="password"
            errors={(response as any).content?.errors}
          />
        </div>

        <div className={isOnMobileApp ? compactFormGroup : formGroup}>
          <label htmlFor="password2">Password (again)</label>
          <input
            id="password2"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            type="password"
            required
          />
          <FormErrors param="password2" errors={password2Errors} />
        </div>

        <Button
          style={{ width: "100%" }}
          disabled={response.fetching}
          onClick={() => submit()}
        >
          Sign Up
        </Button>

        <p className={isOnMobileApp ? linkTextCompact : linkText}>
          Already have an account? <Link to="/account/login">Login here.</Link>
        </p>

        {!isOnMobileApp && (
          <p className={linkText} style={{ marginBottom: 0 }}>
            <a 
              href="https://info.sharpana.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: "#888", 
                fontSize: "0.9em",
                textDecoration: "underline",
                // fontStyle: "italic"
              }}
            >
              What is Sharpana?
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
